import React, { useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import featureToggles from 'config/featureToggles';

import useProcessDetails from 'hooks/useProcessDetails';
import useModal from 'hooks/useModal';

import MainLayout from 'layouts/Main.layout';
import { Row, Col } from 'components/layout';
import { Title } from 'components/Text';
import Table from 'components/Table';
import Icon from 'components/Icon';
import Button from 'components/Button';
import { CameraViewModal } from 'components/process/CameraView';
import { useFeatureToggleItem } from 'components/FeatureToggle';
import StageCard from 'components/process/StageCard';
import ProcessDetailsPageHeader from 'components/process/ProcessPageHeader';
import EquipmentInfoCard from 'components/process/EquipmentInfoCard';

import { formatDuration } from 'helpers/date';

import ContainerSvg from 'assets/images/container.svg';

const ContainerDetails = () => {
  const params = useParams();
  const history = useHistory();
  const cameraViewModal = useModal();
  const individualsFeatureToggle = useFeatureToggleItem(featureToggles.FEATURE_INDIVIDUALS);
  const showIndividuals = individualsFeatureToggle?.visible ?? true;

  const {
    isLoading,
    stages,
    process,
    timeline,
    employees,
    shifts,
  } = useProcessDetails(params.id);

  useEffect(() => {
    const id = parseInt(params.id);
    if (isNaN(id)) {
      history.replace('/unloads');
    }
  }, [params, history]);

  const openCameraView = useCallback((startTs, endTs) => {
    if (process) {
      cameraViewModal.show({
        doorId: process.door_id,
        startTs,
        endTs,
        tzOffset: process.timezone_offset,
      });
    }
  }, [process, cameraViewModal]);

  return (
    <MainLayout>
      <ProcessDetailsPageHeader
        type="container"
        processId={params.id}
        process={process}
        subTitle={
          <Button type="primary" size="small" icon={{ name: 'video', size: 16 }} onClick={() => {
            if (process) {
              let startTs = process.docked_at;
              let endTs = process.undocked_at || Math.floor(new Date() / 1000);

              // NOTE: replace with values from query params if provided
              const queryParams = new URLSearchParams(window.location.search);
              if (queryParams.has('cameraViewStartDt')) {
                startTs = Date.parse(queryParams.get('cameraViewStartDt')) / 1000;
              }
              if (queryParams.has('cameraViewEndDt')) {
                endTs = Date.parse(queryParams.get('cameraViewEndDt')) / 1000;
              }

              cameraViewModal.show({
                doorId: process.door_id,
                startTs,
                endTs,
                tzOffset: process.timezone_offset,
              });
            }
          }}>
            Camera View
          </Button>
        }
      />

      <Row style={{ padding: '12px 24px', flexWrap: 'nowrap' }} justify="space-between" align="stretch">
        <StageCard
          stage='Docked'
          color='#A4B0BA'
          value={stages.docked}
          style={{ flex: 1 }}
        />
        <Icon name="arrow-right" size={24} color={'#A4B0BA'} style={{ alignSelf: 'center' }} />
        <StageCard
          stage="Unload Started"
          color='#A4B0BA'
          value={stages.started}
          style={{ flex: 1 }}
        />
        <Icon name="arrow-right" size={24} color={'#A4B0BA'} style={{ alignSelf: 'center' }} />
        <StageCard
          stage="Unload Finished"
          color='#A4B0BA'
          value={stages.ended}
          style={{ flex: 1 }}
        />
        <Icon name="arrow-right" size={24} color={'#A4B0BA'} style={{ alignSelf: 'center' }} />
        <StageCard
          stage="Undocked"
          color='#A4B0BA'
          value={stages.undocked}
          style={{ flex: 1 }}
        />
      </Row>

      <Row style={{ padding: '12px 24px' }}>
        <Col span={24}>
          <EquipmentInfoCard
            container={process}
            containerTimeline={timeline}
            openCameraView={openCameraView}
            img={ContainerSvg}
          />
        </Col>
      </Row>

      {showIndividuals && (
        <Row style={{ padding: '12px 24px' }}>
          <Col span={24}>
            <Title level={3}>Individuals</Title>
          </Col>
          <Col span={24}>
            <Table
              pagination={false}
              dataSource={employees}
              loading={isLoading}
              rowKey="employee_id"
            >
              <Table.Column type="link" title="Individual" dataIndex="full_name" to={({ item }) => `/individuals/${item.employee_id}`} />
              <Table.Column type="datetime" title="First Seen" dataIndex="first_seen" tzOffset="timezone_offset" />
              <Table.Column type="datetime" title="Last Seen" dataIndex="last_seen" tzOffset="timezone_offset" />
              <Table.Column title="Individual Duration" dataIndex="tot_mins_seen" align="center" render={totMins => {
                if (totMins === undefined || totMins === null) return '-';
                return formatDuration(totMins * 60 * 1000, false);
              }} />
              {/* <Table.Column type="text" title="Shift" dataIndex="shift_no" render={shiftNo => {
                if (typeof shiftNo === 'undefined') return '-';
                return `Shift ${shiftNo}`;
              }} /> */}
            </Table>
          </Col>
        </Row>
      )}

      <Row style={{ padding: '12px 24px' }}>
        <Col span={24}>
          <Title level={3}>Work Segments</Title>
        </Col>
        <Col span={24}>
          <Table
            pagination={false}
            dataSource={shifts}
            loading={isLoading}
            rowKey={item => `${item.start_dt}:${item.end_dt}`}
          >
            <Table.Column type="text" title="Shift" dataIndex="shift_no" render={shiftNo => {
              if (typeof shiftNo === 'undefined') return '-';
              return `${shiftNo}`;
            }} />
            <Table.Column type="datetime" title="Start Date" dataIndex="start_dt" tzOffset="timezone_offset" />
            <Table.Column type="percentage" title="Start %" dataIndex="start_pct" precision={0} />
            <Table.Column type="datetime" title="End Date" dataIndex="end_dt" tzOffset="timezone_offset" />
            <Table.Column type="percentage" title="End %" dataIndex="end_pct" precision={0} />
            <Table.Column type="duration" title="Target Time" dataIndex="target_hrs" inHrs={true} withSecs={false} />
            <Table.Column title="Actual Time" dataIndex="duration_hrs"  render={(e) => {
                const hours = Math.floor(e);
                const minutes = Math.round((e - hours) * 60);
                const hrString = hours >0? `${hours}h `:''
                return `${hrString}${minutes}m`;
             
              }}/>
            <Table.Column type="duration" title="Saved Time" dataIndex="saved_mins" inMins={true} withSecs={false} />
            <Table.Column type="number" title="Target CPH" dataIndex="target_cph" precision={0} />
            <Table.Column type="number" title="Actual CPH" dataIndex="actual_cph" precision={0} />
            <Table.Column type="number" title="Average People" dataIndex="avg_people_active" />
          </Table>
        </Col>
      </Row>

      <CameraViewModal
        {...cameraViewModal.data}
        show={cameraViewModal.isVisible}
        onClose={cameraViewModal.hide}
      />

    </MainLayout>
  );
};

export default ContainerDetails;